import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@tasama/i18n';
import {
  OnboardingHeader,
  InputWithLabel,
  TextareaWithCharacterCount,
  DateInput,
  Dropdown,
  CountryFlag,
  cn,
} from '@tasama/ui';
import {
  createProjectDetailsSchema,
  type ProjectDetailsFormValues,
} from '../schemas/projectDetails.schema';
import { useProjectDetailsStore } from '../stores/project-details.store';
import type { ProjectDetailsStore } from '../stores/project-details.store';

const PROJECT_DETAILS_DEFAULT_VALUES: ProjectDetailsFormValues = {
  projectCodeName: '',
  projectDetails: '',
  expectedLaunchDate: '',
};

export function ProjectDetailsPage() {
  const { t } = useTranslation(['onboarding', 'common']);
  const title = t('projectDetails.title');
  const description = t('projectDetails.description');

  const schema = useMemo(() => createProjectDetailsSchema(t), [t]);

  // Read persisted data — restores fields when navigating back to this step
  const storedFormData = useProjectDetailsStore.getState().formData;

  const {
    watch,
    control,
    setValue,
    setError,
    formState: { errors, isValid },
  } = useForm<ProjectDetailsFormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { ...PROJECT_DETAILS_DEFAULT_VALUES, ...storedFormData },
  });

  // Coordinator store sync — keeps use-step-validation and use-onboarding-payload working
  const setStoreValidity = useProjectDetailsStore(
    (s: ProjectDetailsStore) => s.setValidity
  );
  const setStoreFormData = useProjectDetailsStore(
    (s: ProjectDetailsStore) => s.setFormData
  );
  const pendingApiError = useProjectDetailsStore(
    (s: ProjectDetailsStore) => s.pendingApiError
  );
  const clearApiError = useProjectDetailsStore(
    (s: ProjectDetailsStore) => s.clearApiError
  );

  useEffect(() => {
    setStoreValidity(isValid);
  }, [isValid, setStoreValidity]);

  // Bridge API field errors (e.g. 409 project code name conflict) into RHF
  useEffect(() => {
    if (pendingApiError) {
      setError(pendingApiError.field as keyof ProjectDetailsFormValues, {
        type: 'server',
        message: pendingApiError.message,
      });
      clearApiError();
    }
  }, [pendingApiError, setError, clearApiError]);

  useEffect(() => {
    const subscription = watch((values) => {
      setStoreFormData(values as Partial<ProjectDetailsFormValues>);
    });
    return () => subscription.unsubscribe();
  }, [watch, setStoreFormData]);

  const formValues = useWatch({ control });

  const handleFieldChange = (
    field: keyof ProjectDetailsFormValues,
    value: string
  ) => {
    setValue(field, value, { shouldValidate: true, shouldTouch: true });
  };

  return (
    <div className="space-y-6">
      <OnboardingHeader
        size="lg"
        title={title}
        description={description}
        icon="Bell"
        testID="project-details-header"
      />

      <div className="space-y-6">
        <form className="space-y-6" noValidate>
          <div className="grid grid-cols-1 gap-6">
            <InputWithLabel
              label={t('projectDetails.fields.projectCodeName.label')}
              id="projectName"
              placeholder={t(
                'projectDetails.fields.projectCodeName.placeholder'
              )}
              required
              value={formValues.projectCodeName || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldChange('projectCodeName', e.target.value)
              }
              error={!!errors.projectCodeName}
              errorMessage={errors.projectCodeName?.message}
              testID="project-name-field"
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            <Dropdown
              id="projectCountry"
              label={t('projectDetails.fields.country.label')}
              options={[
                {
                  label: t('common:companyForm.saudiArabia'),
                  value: 'SA',
                  icon: <CountryFlag countryCode="SA" />,
                },
              ]}
              value="SA"
              onChange={() => {}}
              disabled
              showLeftIcon={false}
              testID="project-country-field"
            />
          </div>

          <div className="space-y-2">
            <TextareaWithCharacterCount
              label={t('projectDetails.fields.projectDetails.label')}
              id="project-description"
              placeholder={t(
                'projectDetails.fields.projectDetails.placeholder'
              )}
              value={formValues.projectDetails || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleFieldChange('projectDetails', e.target.value)
              }
              error={!!errors.projectDetails}
              {...(errors.projectDetails && {
                errorMessage: errors.projectDetails.message,
              })}
              testID="project-description-field"
              rows={4}
              maxLength={1000}
              showCharacterCount={true}
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            <DateInput
              label={t('projectDetails.fields.expectedLaunchDate.label')}
              id="expected-launch-date"
              placeholder={t('dateInput.placeholder', { ns: 'common' })}
              error={!!errors.expectedLaunchDate}
              {...(errors.expectedLaunchDate && {
                errorMessage: errors.expectedLaunchDate.message,
              })}
              className={cn(
                formValues.expectedLaunchDate ? 'date-input--has-value' : ''
              )}
              testID="expected-launch-date-field"
              title={t('projectDetails.fields.expectedLaunchDate.label')}
              value={formValues.expectedLaunchDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldChange('expectedLaunchDate', e.target.value)
              }
              disabledDays={{ before: new Date() }}
              startMonth={new Date()}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProjectDetailsPage;
